import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2 } from "lucide-react";
import { formatBytes } from "@/utils/file.utils";
import { cn } from "@/utils/cn";
import type { IChatAttachment } from "@/types/(my-space)/chat.interface";
import { fileBadge, type FileTone } from "../_lib/file-badge";
import { downloadChatFile } from "../_lib/download-file";

const TONE: Record<FileTone, string> = {
  primary: "bg-primary text-primary-foreground",
  accent: "bg-accent text-accent-foreground",
  danger: "bg-danger text-danger-foreground",
  warning: "bg-warning text-warning-foreground",
  info: "bg-info text-info-foreground",
  success: "bg-success text-success-foreground",
};

/** Rounded tile at a card's start: an icon, or a file's extension in bold. */
export const CardTile = ({
  tone,
  isMine,
  children,
}: {
  tone: FileTone;
  isMine: boolean;
  children: ReactNode;
}) => (
  <span
    className={cn(
      "flex size-10.5 shrink-0 items-center justify-center rounded-[11px] text-[10.5px] font-extrabold tracking-wide [&_svg]:size-5",
      // A primary tile would vanish on my own (primary) bubble.
      tone === "primary" && isMine ? "bg-primary-foreground text-primary" : TONE[tone],
    )}
  >
    {children}
  </span>
);

/** Round action at a card's end (download, open). */
export const CardAction = ({
  isMine,
  busy,
  children,
}: {
  isMine: boolean;
  busy?: boolean;
  children: ReactNode;
}) => (
  <span
    className={cn(
      "flex size-8 shrink-0 items-center justify-center rounded-full transition-colors [&_svg]:size-4",
      isMine
        ? "bg-primary-foreground/20 group-hover:bg-primary-foreground/30"
        : "bg-primary-muted text-primary group-hover:bg-primary/15",
    )}
  >
    {busy ? <Loader2 className="animate-spin" /> : children}
  </span>
);

type CardTarget =
  | { href: string; onClick?: never; disabled?: never }
  | { href?: never; onClick?: () => void; disabled?: boolean };

/**
 * The card every file, link, shortcut and location shares (the app's design):
 * a coloured tile, a bold title over a quieter line, a round action — and the
 * whole card is the target. `top` sits above the row inside the card (a map).
 */
export const RichCard = ({
  isMine,
  leading,
  title,
  subtitle,
  subtitleLtr,
  action,
  top,
  meta,
  label,
  className,
  ...target
}: CardTarget & {
  isMine: boolean;
  leading: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** URLs and hosts read left-to-right in Arabic too. */
  subtitleLtr?: boolean;
  action?: ReactNode;
  top?: ReactNode;
  /** Under the subtitle (a status badge). */
  meta?: ReactNode;
  /** Accessible name when the visible title isn't enough. */
  label?: string;
  className?: string;
}) => {
  const body = (
    <>
      {top}
      <span className="flex items-center gap-3 p-2.5">
        {leading}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="line-clamp-2 text-sm leading-tight font-semibold break-words">
            {title}
          </span>
          {subtitle ? (
            <span
              dir={subtitleLtr ? "ltr" : undefined}
              className={cn(
                "truncate text-xs",
                subtitleLtr && "text-start rtl:text-end",
                isMine ? "text-primary-foreground/75" : "text-muted-foreground",
              )}
            >
              {subtitle}
            </span>
          ) : null}
          {meta ? <span className="mt-1 flex">{meta}</span> : null}
        </span>
        {action}
      </span>
    </>
  );
  const classes = cn(
    "group flex w-64 max-w-full flex-col overflow-hidden rounded-[14px] text-start transition-colors",
    isMine
      ? "bg-primary-foreground/15 hover:bg-primary-foreground/20"
      : "bg-muted text-foreground hover:bg-muted/70",
    className,
  );
  if (target.href) {
    return (
      <a
        href={target.href}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={label}
        className={classes}
      >
        {body}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={target.onClick}
      disabled={target.disabled}
      aria-label={label}
      className={cn(classes, "disabled:cursor-default")}
    >
      {body}
    </button>
  );
};

/**
 * A file as a card: its type tile, name, "6.2 MB · ZIP" and Download. A
 * document is never a bare button (CLAUDE.md "File / document rows").
 */
export const FileCard = ({
  attachment: a,
  isMine,
  className,
}: {
  attachment: Pick<IChatAttachment, "fileName" | "fileUrl" | "size" | "kind">;
  isMine: boolean;
  className?: string;
}) => {
  const { t } = useTranslation("chat");
  const [downloading, setDownloading] = useState(false);
  const badge = fileBadge(a.fileName, a.kind);
  const download = async () => {
    setDownloading(true);
    await downloadChatFile(a.fileUrl, a.fileName);
    setDownloading(false);
  };
  return (
    <RichCard
      isMine={isMine}
      className={className}
      onClick={() => void download()}
      disabled={downloading}
      label={t("card.download_file", { name: a.fileName })}
      leading={
        <CardTile tone={badge.tone} isMine={isMine}>
          {badge.label}
        </CardTile>
      }
      title={a.fileName}
      subtitle={[a.size ? formatBytes(a.size) : null, badge.label].filter(Boolean).join(" · ")}
      // "2.3 MB · PDF" — Arabic bidi would otherwise print "MB · PDF 2.3".
      subtitleLtr
      action={
        <CardAction isMine={isMine} busy={downloading}>
          <Download />
        </CardAction>
      }
    />
  );
};
